import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// WhatsApp business number (without +, country code 91 for India)
const WHATSAPP_NUMBER = '917867096977';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page-aware pre-filled messages
const PAGE_MESSAGES = {
  '/': "Hi VB Global, I'd like to know more about your products. Please share details.",
  '/products/paper-chemicals': "Hi, I'm interested in your Paper Chemicals. Please share details and pricing.",
  '/products/construction-chemicals': "Hi, I'm interested in your Construction Chemicals. Please share details and pricing.",
  '/products/ss-wire-mesh': "Hi, I'm interested in your SS Wire Mesh. Please share details and pricing.",
  '/products/high-pressure-showers': "Hi, I'm interested in your High Pressure Showers & Nozzles. Please share details and pricing.",
  '/solutions': "Hi VB Global, I'm interested in your industrial solutions. Please share more information.",
  '/about': "Hi VB Global, I'd like to know more about your company and products.",
  '/contact': "Hi VB Global, I'd like to get in touch regarding your products.",
};

const getMessageForPage = (pathname) => {
  return PAGE_MESSAGES[pathname] || PAGE_MESSAGES['/'];
};

// Check if user is on mobile device
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Build appropriate WhatsApp link based on device
export const buildWhatsAppLink = (message) => {
  const encoded = encodeURIComponent(message);
  if (isMobileDevice()) {
    // Mobile: Direct app link via wa.me
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
  }
  // Desktop: Direct to WhatsApp Web (bypasses api.whatsapp.com redirect)
  return `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encoded}`;
};

// Track WhatsApp click event in backend
export const trackWhatsAppClick = async (source, page) => {
  try {
    await axios.post(`${API}/track-whatsapp-click`, {
      source: source,
      page: page
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.debug('WhatsApp click tracking failed:', error.message);
  }
};

const FloatingWhatsAppButton = () => {
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Show tooltip briefly after page load
    const timer = setTimeout(() => setShowTooltip(true), 2000);
    const hideTimer = setTimeout(() => setShowTooltip(false), 7000);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, [location.pathname]);

  const message = getMessageForPage(location.pathname);
  const whatsappLink = buildWhatsAppLink(message);

  const handleClick = () => {
    trackWhatsAppClick('floating_button', location.pathname);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-end gap-3">
      {showTooltip && (
        <div 
          className="bg-white shadow-xl rounded-lg p-3 max-w-[220px] border border-gray-200 animate-fade-in mb-2"
          data-testid="whatsapp-tooltip"
        >
          <button 
            onClick={() => setShowTooltip(false)}
            className="absolute top-1 right-2 text-gray-400 hover:text-gray-600 text-sm"
            aria-label="Close"
          >
            ×
          </button>
          <p className="text-sm text-gray-800 font-medium mb-1">Need help?</p>
          <p className="text-xs text-gray-600">Chat with us on WhatsApp for instant response!</p>
        </div>
      )}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-200"
        aria-label="Chat on WhatsApp"
        data-testid="floating-whatsapp-button"
        onMouseEnter={() => setShowTooltip(true)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default FloatingWhatsAppButton;
